<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Announcements', to: ROUTES.ADMIN.ANNOUNCEMENTS.ANNOUNCEMENT_LIST },
            { title: isCreating ? 'Create' : 'Edit', to: route.fullPath }
          ]"
        />
      </div>

      <!-- Form Card -->
      <div class="flex flex-col space-y-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div>
          <h2 class="text-xl font-bold text-gray-800">
            {{ isCreating ? 'Create New Announcement' : 'Edit Announcement' }}
          </h2>
          <p class="mt-1 text-sm text-gray-600">
            Configure a sitewide announcement banner that will appear across the application
          </p>
        </div>

        <form
          class="space-y-6"
          @submit.prevent="handleSubmit"
        >
          <!-- Message -->
          <div>
            <TextAreaField
              name="message"
              label="Message"
              placeholder="Enter the announcement message..."
              rows="3"
              asterisk
            />
          </div>

          <!-- Message Type -->
          <SelectField
            name="message_type"
            label="Message Type"
            :options="messageTypeOptions"
            asterisk
          />

          <!-- Active & Dismissible -->
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <CheckboxField
              v-model="values.is_active"
              name="is_active"
              label="Active"
            />
            <CheckboxField
              v-model="values.dismissible"
              name="dismissible"
              label="Dismissible"
            />
          </div>

          <!-- Date Range -->
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <InputField
              name="start_date"
              label="Start Date (Optional)"
              type="datetime-local"
            />
            <InputField
              name="end_date"
              label="End Date (Optional)"
              type="datetime-local"
            />
          </div>

          <!-- Actions -->
          <div class="flex items-center justify-between gap-4 border-t pt-6">
            <button
              v-if="!isCreating"
              type="button"
              class="inline-flex items-center gap-x-2 rounded-lg border border-red-600 px-4 py-2 text-sm font-semibold
                text-red-600 hover:bg-red-50 disabled:pointer-events-none disabled:opacity-50"
              @click="handleDelete"
            >
              Delete
            </button>
            <div class="ml-auto flex gap-2">
              <router-link
                :to="ROUTES.ADMIN.ANNOUNCEMENTS.ANNOUNCEMENT_LIST"
                class="inline-flex items-center gap-x-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm
                  font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:pointer-events-none
                  disabled:opacity-50"
              >
                Cancel
              </router-link>
              <button
                type="submit"
                :disabled="loading"
                class="inline-flex items-center gap-x-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white
                  shadow-sm hover:bg-purple-700 disabled:pointer-events-none disabled:opacity-50"
              >
                {{ loading ? 'Saving...' : isCreating ? 'Create' : 'Save' }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, computed } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { ROUTES } from '@/router/routes'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import SelectField, { type Option } from '@/components/admin/form/SelectField.vue'
  import { AnnouncementsService } from '@/types/generated/services.gen'
  import { useToast } from '@/components/ui/toast/use-toast'
  import { useForm } from 'vee-validate'
  import * as yup from 'yup'
  import type { SuperUserAnnouncement } from '@/types/generated/types.gen'

  const route = useRoute()
  const router = useRouter()
  const { toast } = useToast()

  const loading = ref(false)
  const isCreating = computed(() => route.params.uuid === 'create')

  const messageTypeOptions: Option[] = [
    { value: 'INFO', title: 'Info' },
    { value: 'SUCCESS', title: 'Success' },
    { value: 'WARNING', title: 'Warning' },
    { value: 'ERROR', title: 'Error' }
  ]

  const validationSchema = yup.object({
    message: yup.string().required('Message is required'),
    message_type: yup.string().required('Message type is required'),
    is_active: yup.boolean(),
    dismissible: yup.boolean(),
    start_date: yup.string().nullable(),
    end_date: yup.string().nullable()
  })

  const {
    handleSubmit: veeHandleSubmit,
    setValues,
    values
  } = useForm({
    validationSchema,
    initialValues: {
      message: '',
      message_type: 'INFO',
      is_active: true,
      dismissible: true,
      start_date: null,
      end_date: null
    }
  })

  const fetchAnnouncement = async () => {
    if (isCreating.value) return

    try {
      const response = await AnnouncementsService.announcementsRetrieve({
        uuid: route.params.uuid as string
      })

      const announcement = response as unknown as SuperUserAnnouncement
      setValues({
        message: announcement.message,
        message_type: announcement.message_type,
        is_active: announcement.is_active,
        dismissible: announcement.dismissible,
        start_date: announcement.start_date
          ? (new Date(announcement.start_date).toISOString().slice(0, 16) as any)
          : undefined,
        end_date: announcement.end_date
          ? (new Date(announcement.end_date).toISOString().slice(0, 16) as any)
          : undefined
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load announcement',
        variant: 'destructive'
      })
    }
  }

  const handleSubmit = veeHandleSubmit(async (formValues) => {
    loading.value = true

    try {
      const payload = {
        ...formValues,
        start_date: formValues.start_date ? new Date(formValues.start_date).toISOString() : null,
        end_date: formValues.end_date ? new Date(formValues.end_date).toISOString() : null
      }

      if (isCreating.value) {
        await AnnouncementsService.announcementsCreate({
          requestBody: payload as any
        })
        toast({
          title: 'Success',
          description: 'Announcement created successfully'
        })
      } else {
        await AnnouncementsService.announcementsPartialUpdate({
          uuid: route.params.uuid as string,
          requestBody: payload as any
        })
        toast({
          title: 'Success',
          description: 'Announcement updated successfully'
        })
      }

      router.push(ROUTES.ADMIN.ANNOUNCEMENTS.ANNOUNCEMENT_LIST)
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to save announcement',
        variant: 'destructive'
      })
    } finally {
      loading.value = false
    }
  })

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this announcement?')) return

    loading.value = true
    try {
      await AnnouncementsService.announcementsDestroy({
        uuid: route.params.uuid as string
      })
      toast({
        title: 'Success',
        description: 'Announcement deleted successfully'
      })
      router.push(ROUTES.ADMIN.ANNOUNCEMENTS.ANNOUNCEMENT_LIST)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete announcement',
        variant: 'destructive'
      })
    } finally {
      loading.value = false
    }
  }

  onMounted(() => {
    fetchAnnouncement()
  })
</script>
